import React, { useEffect, useRef, useState } from 'react'
import moment from 'moment'
import Highcharts, { Options, Chart } from 'highcharts'
import HighchartsReact from 'highcharts-react-official'


interface Data {
    startDate: string
    cost: number
}
interface Props {
    data: { planned: Data[]; executed: Data[] }
}

function CostChart({ data }: Props) {
    const chartInstanceRef = useRef<Chart>(null)
    const [chartState, setChartState] = useState<Options>(chartOptions)

    const formatData = (data: {
        planned: Data[]
        executed: Data[]
        p10: Data[]
        p25: Data[]
        p50: Data[]
        p75: Data[]
        p90: Data[]
    }) => {
        const chartData: any = {
            planned: {
                name: 'Planned',
                data: []
            },
            executed: {
                name: 'Executed',
                color:'#000',
                data: []
            },
            preview: {
                name: 'Forecast',
                color:'#5f5f5f',
                data: []
            },
            p10: {
                name: 'P10',
                color: 'rgba(0,128,0, 0.5)',
                data: [],
            },
            p25: {
                name: 'P25',
                color: 'rgba(191,255,0,0.5)',
                data: []
            },
            p50: {
                name: 'P50',
                color: 'rgba(255, 255, 0, 0.5)',
                data: []
            },
            p75: {
                name: 'P75',
                color: 'rgba(255,165,0,0.5)',
                data: []
            },
            p90: {
                name: 'P90',
                color: 'rgba(255,0,0, 0.5)',
                data: []
            }
        }

        let totalPlannedCost = 0
        const { planned, executed, p10, p25, p50, p75, p90 } = data

        const minDate =
            moment(planned[0].startDate).diff(moment(executed[0].startDate)) < 0
                ? moment(planned[0].startDate)
                : moment(executed[0].startDate)

        for (let i = 0; i < planned.length; i++) {
            let previewValue = null
            let executedValue = null
            let plannedValue = i - 1 in planned ? chartData.planned.data[i - 1].y + planned[i].cost : planned[i].cost

            let p10Value = i - 1 in p10 ? chartData.p10.data[i - 1].y + p10[i].cost : p10[i].cost
            let p25Value = i - 1 in p25 ? chartData.p25.data[i - 1].y + p25[i].cost : p25[i].cost
            let p50Value = i - 1 in p50 ? chartData.p50.data[i - 1].y + p50[i].cost : p50[i].cost
            let p75Value = i - 1 in p75 ? chartData.p75.data[i - 1].y + p75[i].cost : p75[i].cost
            let p90Value = i - 1 in p90 ? chartData.p90.data[i - 1].y + p90[i].cost : p90[i].cost

            let executedDate = null
            let plannedDate = moment(planned[i].startDate).diff(minDate, 'days')
            let previewDate = plannedDate
            if (i in executed) {
                executedValue =
                    i - 1 in executed ? chartData.executed.data[i - 1].y + executed[i].cost : executed[i].cost
                executedDate = moment(executed[i].startDate).diff(minDate, 'days')
                if (i + 1 === executed.length) {
                    previewValue = executedValue
                    previewDate = executedDate
                }
            } else {
                if (i - 1 in executed) {
                    previewValue = chartData.executed.data[i - 1].y + planned[i].cost
                } else if (i - 1 in chartData.preview.data) {
                    previewValue = chartData.preview.data[i - 1].y + planned[i].cost
                } else {
                    previewValue = planned[i].cost
                }
            }

            totalPlannedCost += planned[i].cost

            chartData.preview.data.push({ x: previewDate, y: previewValue })
            chartData.executed.data.push({ x: executedDate, y: executedValue })
            chartData.planned.data.push({ x: plannedDate, y: plannedValue })

            chartData.p10.data.push({ x: plannedDate, y: p10Value })
            chartData.p25.data.push({ x: plannedDate, y: p25Value })
            chartData.p50.data.push({ x: plannedDate, y: p50Value })
            chartData.p75.data.push({ x: plannedDate, y: p75Value })
            chartData.p90.data.push({ x: plannedDate, y: p90Value })
        }
        delete chartData.planned
        return Object.values(chartData)
    }

    useEffect(() => {
        const chartData = data ? formatData(data) : []
        setChartState({
            series: chartData
        })
    }, [data])

    return (
        <div>
            <HighchartsReact
                highcharts={Highcharts}
                options={chartState}
                allowChartUpdate={true}
                callback={(chart: any) => {
                    chartInstanceRef.current = chart
                }}
            />
        </div>
    )
}

export default CostChart

const chartOptions: Options = {
    chart: {
        type: 'line',
        height: 350
    },
    title: {
        text: ''
    },
    credits: {
        enabled: false
    },

    xAxis: {
        type: 'category',
        gridLineWidth: 1,
        labels: {
            overflow: 'justify'
        },
        title: {
            text: 'Days',
            align: 'high'
        }
    },
    legend: {
        layout: 'horizontal',
        align: 'right',
        verticalAlign: 'top'
    },

    yAxis: {
        min: 0,
        minorTicks: true,
        title: {
            text: 'Total Cost',
            align: 'high',
            rotation: 0
        },
        labels: {
            format: 'USD {value}'
        }
    },

    tooltip: {
        shared: true
    },

    plotOptions: {
        series: {
            animation: false,
            marker: {
                enabled: false
            }
        }
    }
}
